import { useState, useEffect, useCallback } from 'react'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useAccount } from 'wagmi'
import { Icon } from '../components/Layout'
import {
  getAllocationToken,
  getAllocationEmail,
  clearAllocationSession,
  requestAllocationCode,
  verifyAllocationCode,
} from '../services/allocationAuth'
import {
  getSeasonsWithSnapshot,
  getMyAllocation,
  getSeasonLeaderboard,
  registerPayoutWallet,
} from '../services/seasonAllocation'

const TIER_STYLE = {
  gold:   { icon: 'workspace_premium', text: 'text-primary-container' },
  silver: { icon: 'workspace_premium', text: 'text-on-surface-variant' },
  bronze: { icon: 'workspace_premium', text: 'text-amber-700' },
  diamond:{ icon: 'workspace_premium', text: 'text-blue-400' },
}

function tierInfo(tier) {
  return TIER_STYLE[String(tier ?? '').toLowerCase()] ?? TIER_STYLE.bronze
}

function fmtNumber(n) {
  return Number(n ?? 0).toLocaleString()
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-surface-container">
      <header className="bg-surface border-b-4 border-on-background px-4 sm:px-8 py-4 flex items-center gap-3">
        <img src="/favicon.jpg" alt="Chicago" className="w-8 h-8 object-cover border-2 border-on-background" />
        <span className="font-extrabold text-[16px] uppercase tracking-tight">Chicago</span>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-6">
        {children}
      </main>
    </div>
  )
}

// ─── Stage 1: OTP login ────────────────────────────────────────────────
function LoginStage({ onVerified }) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [challengeId, setChallengeId] = useState(null)
  const [stage, setStage] = useState('email') // 'email' | 'code'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const sendCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await requestAllocationCode(email.trim().toLowerCase())
      setChallengeId(res.challengeId)
      setStage('code')
    } catch (err) {
      setError(err.message ?? 'Failed to send code')
    } finally {
      setLoading(false)
    }
  }

  const verify = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await verifyAllocationCode(challengeId, email.trim().toLowerCase(), code.trim())
      onVerified()
    } catch (err) {
      setError(err.message ?? 'Invalid code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="border-4 border-on-background bg-surface rounded-lg p-8 flex flex-col items-center gap-4 text-center max-w-md mx-auto w-full" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
      <h1 className="font-extrabold text-[22px] uppercase tracking-tight">Season Allocation</h1>
      <p className="text-[13px] text-on-surface-variant -mt-2">Check your season results and rewards.</p>

      {error && (
        <div className="w-full border-2 border-error bg-error-container text-on-error-container px-3 py-2 text-[12px] font-bold text-left">
          {error}
        </div>
      )}

      {stage === 'email' ? (
        <form onSubmit={sendCode} className="w-full flex flex-col gap-3">
          <input
            type="email"
            required
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-surface-container border-2 border-on-background px-4 py-3 text-[14px] focus:outline-none focus:border-primary-container"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-3 bg-primary-container text-on-primary-fixed font-extrabold text-[12px] uppercase tracking-widest border-2 border-on-background hover:brightness-105 transition-all disabled:opacity-50"
          >
            {loading ? 'Sending…' : 'Send Code'}
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="w-full flex flex-col gap-3">
          <p className="text-[12px] text-on-surface-variant">Code sent to {email}</p>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            required
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="bg-surface-container border-2 border-on-background px-4 py-3 text-[18px] tracking-[0.3em] text-center focus:outline-none focus:border-primary-container"
          />
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="px-5 py-3 bg-primary-container text-on-primary-fixed font-extrabold text-[12px] uppercase tracking-widest border-2 border-on-background hover:brightness-105 transition-all disabled:opacity-50"
          >
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button type="button" onClick={() => { setStage('email'); setCode(''); setError(null) }} className="text-[11px] text-on-surface-variant underline">
            Use a different email
          </button>
        </form>
      )}
    </div>
  )
}

// ─── Stage 2: Your Result ──────────────────────────────────────────────
function ResultStage({ seasons, seasonId, setSeasonId, onViewLeaderboard, onLogout }) {
  const { address, isConnected } = useAccount()
  const [allocation, setAllocation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [registering, setRegistering] = useState(false)
  const [registerError, setRegisterError] = useState(null)
  const [justRegistered, setJustRegistered] = useState(false)

  const load = useCallback(() => {
    if (!seasonId) return
    setLoading(true)
    getMyAllocation(seasonId).then(setAllocation).finally(() => setLoading(false))
  }, [seasonId])

  useEffect(() => { load() }, [load])

  const handleRegister = async () => {
    if (!address) return
    setRegistering(true)
    setRegisterError(null)
    try {
      await registerPayoutWallet(seasonId, address)
      setJustRegistered(true)
      load()
    } catch (err) {
      setRegisterError(err.message ?? 'Failed to register wallet')
    } finally {
      setRegistering(false)
    }
  }

  const tier = allocation?.eligible ? tierInfo(allocation.tier) : null
  const walletRegistered = allocation?.eligible && (allocation.walletRegistered || justRegistered)
  const claimOpen = allocation?.eligible && allocation.claimOpen

  return (
    <div className="flex flex-col gap-5 max-w-md mx-auto w-full">
      <div className="flex items-center justify-between">
        <h1 className="font-extrabold text-[22px] uppercase tracking-tight">Your Result</h1>
        <button onClick={onLogout} className="text-[11px] text-on-surface-variant underline">Log out</button>
      </div>

      {seasons.length > 1 && (
        <select
          value={seasonId ?? ''}
          onChange={(e) => setSeasonId(e.target.value)}
          className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] font-bold focus:outline-none"
        >
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{s.seasonName}</option>
          ))}
        </select>
      )}

      {loading ? (
        <div className="h-48 bg-surface-container-high animate-pulse rounded-lg" />
      ) : !allocation?.eligible ? (
        <div className="border-4 border-dashed border-on-background/25 bg-surface p-8 text-center flex flex-col items-center gap-2">
          <Icon name="info" className="text-[32px] text-on-surface-variant" />
          <p className="font-extrabold text-[15px]">You're not eligible for this season's payout</p>
          <p className="text-[12px] text-on-surface-variant">Only users who participated and ranked appear here.</p>
        </div>
      ) : (
        <div className="border-4 border-on-background bg-surface rounded-lg p-8 flex flex-col items-center gap-4 text-center" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <Icon name="celebration" className="text-[36px] text-primary-container" />
          <p className="font-extrabold text-[18px]">Congratulations!</p>
          <span className={`px-3 py-1 border rounded text-[11px] font-bold uppercase tracking-widest ${tier.text}`}>
            {allocation.tier ?? 'Ranked'} · Rank #{allocation.rank}
          </span>
          <div>
            <p className="font-extrabold text-[22px] leading-none text-primary-container">{fmtNumber(allocation.cptReward)}</p>
            <p className="text-[10px] uppercase tracking-widest text-on-surface-variant mt-1">CPT Reward</p>
          </div>

          <div className="w-full border-t-2 border-on-background/10 my-1" />

          {allocation.paid ? (
            <p className="font-bold text-[13px]">Payout sent to {allocation.walletAddress}</p>
          ) : walletRegistered ? (
            <div className="flex flex-col items-center gap-1">
              <p className="font-bold text-[13px]">Wallet registered — payout pending</p>
              <p className="text-[11px] text-on-surface-variant font-mono">{allocation.walletAddress || address}</p>
            </div>
          ) : (
            <>
              {registerError && (
                <div className="w-full border-2 border-error bg-error-container text-on-error-container px-3 py-2 text-[12px] font-bold text-left">
                  {registerError}
                </div>
              )}
              <ConnectButton.Custom>
                {({ openConnectModal, mounted }) => {
                  if (!mounted) return null
                  const disabled = !claimOpen || registering
                  const label = !claimOpen
                    ? 'Wallet submission opens soon'
                    : !isConnected
                      ? 'Connect Wallet'
                      : registering
                        ? 'Submitting…'
                        : 'Submit This Wallet'
                  return (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => { if (!claimOpen) return; if (!isConnected) openConnectModal?.(); else handleRegister() }}
                      className="flex items-center gap-2 px-6 py-3 bg-primary-container text-on-primary-fixed font-extrabold text-[12px] uppercase tracking-widest border-4 border-on-background disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
                      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}
                    >
                      <Icon name="account_balance_wallet" className="text-[16px]" />
                      {label}
                    </button>
                  )
                }}
              </ConnectButton.Custom>
            </>
          )}
        </div>
      )}

      <button onClick={onViewLeaderboard} className="text-center text-[12px] font-bold uppercase tracking-widest text-primary-container hover:underline">
        View Leaderboard →
      </button>
    </div>
  )
}

// ─── Stage 3: Leaderboard (view-only, no reward amounts) ───────────────
const TIER_BADGE_STYLE = {
  gold:    { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  silver:  { text: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' },
  bronze:  { text: 'text-orange-800', bg: 'bg-orange-50', border: 'border-orange-200' },
  diamond: { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
}
function tierBadgeStyle(tier) {
  return TIER_BADGE_STYLE[String(tier ?? '').toLowerCase()] ?? TIER_BADGE_STYLE.bronze
}

const PODIUM_LOOK = {
  1: {
    ring: 'ring-amber-300',
    badge: 'bg-gradient-to-br from-amber-300 to-amber-500 text-white',
    card: 'bg-white border-2 border-amber-300 shadow-lg pt-10 pb-6 px-5',
    order: 'order-1 sm:order-2',
  },
  2: {
    ring: 'ring-slate-300',
    badge: 'bg-gradient-to-br from-slate-300 to-slate-400 text-white',
    card: 'bg-white border border-neutral-200 shadow-sm pt-9 pb-5 px-4',
    order: 'order-2 sm:order-1',
  },
  3: {
    ring: 'ring-orange-300',
    badge: 'bg-gradient-to-br from-orange-300 to-orange-500 text-white',
    card: 'bg-white border border-neutral-200 shadow-sm pt-9 pb-5 px-4',
    order: 'order-3',
  },
}

function TopThreePodium({ entries }) {
  if (entries.length !== 3) return null
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 pt-4">
      {entries.map((entry) => {
        const look = PODIUM_LOOK[entry.rank]
        const tierStyle = tierBadgeStyle(entry.tier)
        return (
          <div key={entry.rank} className={`flex-1 flex flex-col ${look.order}`}>
            <div className={`relative flex flex-col items-center text-center rounded-2xl gap-1.5 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl ${look.card}`}>
              <div className={`absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[14px] shadow-md ${look.badge}`}>
                {entry.rank}
              </div>
              <div className={`w-16 h-16 rounded-full overflow-hidden bg-surface-container ring-4 ${look.ring} mt-2`}>
                <img src="/favicon.jpg" alt={entry.username} className="w-full h-full object-cover" />
              </div>
              <p className="font-extrabold text-[15px] text-neutral-900 mt-1 truncate max-w-full">{entry.username}</p>
              <span className={`inline-flex items-center gap-1 font-bold text-[10px] uppercase tracking-widest ${tierStyle.text}`}>
                <Icon name="workspace_premium" className="text-[13px]" />
                {entry.tier ?? ''}
              </span>
              <p className="font-extrabold text-[20px] text-neutral-900 mt-1 leading-none">{fmtNumber(entry.score)}</p>
              <p className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">Score</p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LeaderboardRow({ entry, isYou }) {
  const tierStyle = tierBadgeStyle(entry.tier)
  return (
    <div
      className={`grid grid-cols-[36px_1fr_70px] sm:grid-cols-[56px_1fr_100px_100px] items-center gap-2 px-4 sm:px-6 py-3.5 border-b border-neutral-100 transition-colors duration-150 hover:bg-amber-50/40 ${isYou ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : 'odd:bg-white even:bg-neutral-50/50'}`}
    >
      <div className="flex items-center gap-1.5">
        {isYou && (
          <span className="px-1.5 py-0.5 rounded bg-amber-400 text-white text-[9px] font-extrabold uppercase tracking-widest">You</span>
        )}
        <span className="font-bold text-[13px] text-neutral-700">{entry.rank}</span>
      </div>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded-full overflow-hidden bg-surface-container shrink-0">
          <img src="/favicon.jpg" alt={entry.username} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="font-bold text-[13px] text-neutral-900 truncate">{entry.username}</span>
          {entry.tier && (
            <span className={`sm:hidden inline-flex items-center gap-0.5 font-bold text-[8px] uppercase tracking-widest w-fit ${tierStyle.text}`}>
              <Icon name="workspace_premium" className="text-[10px]" />
              {entry.tier}
            </span>
          )}
        </div>
      </div>
      <span className={`hidden sm:inline-flex items-center gap-1 font-bold text-[9px] uppercase tracking-widest w-fit ${tierStyle.text}`}>
        <Icon name="workspace_premium" className="text-[12px]" />
        {entry.tier ?? ''}
      </span>
      <span className="text-[13px] font-bold text-neutral-700 text-right">{fmtNumber(entry.score)}</span>
    </div>
  )
}

function LeaderboardTable({ rows, loading, myRank }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
      <div className="sticky top-0 z-10 grid grid-cols-[36px_1fr_70px] sm:grid-cols-[56px_1fr_100px_100px] gap-2 px-4 sm:px-6 py-3 bg-neutral-900 text-white text-[10px] font-extrabold uppercase tracking-widest">
        <span>Rank</span>
        <span>User</span>
        <span className="hidden sm:inline">Tier</span>
        <span className="text-right">Score</span>
      </div>
      {rows.map((r) => (
        <LeaderboardRow key={r.rank} entry={r} isYou={myRank != null && r.rank === myRank} />
      ))}
      {loading && <div className="h-24 animate-pulse bg-neutral-50" />}
    </section>
  )
}

function pageList(current, total) {
  const delta = 1
  const range = []
  const withDots = []
  let last
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i)
    }
  }
  for (const i of range) {
    if (last != null) {
      if (i - last === 2) withDots.push(last + 1)
      else if (i - last > 2) withDots.push('…')
    }
    withDots.push(i)
    last = i
  }
  return withDots
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-center gap-1.5 flex-wrap">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 disabled:opacity-30 hover:bg-neutral-50 transition-colors"
      >
        <Icon name="chevron_left" className="text-[18px]" />
      </button>
      {pageList(page, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`dots-${i}`} className="w-9 h-9 flex items-center justify-center text-neutral-400 text-[12px] font-bold">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-9 h-9 flex items-center justify-center rounded-lg text-[12px] font-extrabold transition-colors ${
              p === page ? 'bg-neutral-900 text-white' : 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="w-9 h-9 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 disabled:opacity-30 hover:bg-neutral-50 transition-colors"
      >
        <Icon name="chevron_right" className="text-[18px]" />
      </button>
    </div>
  )
}

function LeaderboardStage({ seasons, seasonId, setSeasonId, onBack }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [myRank, setMyRank] = useState(null)
  const LIMIT = 20
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const load = useCallback((pageNum) => {
    if (!seasonId) return
    setLoading(true)
    const nextOffset = (pageNum - 1) * LIMIT
    getSeasonLeaderboard(seasonId, nextOffset, LIMIT)
      .then((res) => {
        setTotal(res.total)
        setRows(res.rows)
      })
      .finally(() => setLoading(false))
  }, [seasonId])

  useEffect(() => { setPage(1); load(1) }, [seasonId, load])

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return
    setPage(p)
    load(p)
  }

  // Reuses the same getMyAllocation call ResultStage already makes — just
  // to know which row (by rank) is "you" for highlighting. No new endpoint,
  // and this still never surfaces cptReward on this screen.
  useEffect(() => {
    if (!seasonId) return
    let cancelled = false
    getMyAllocation(seasonId).then((a) => {
      if (!cancelled) setMyRank(a?.eligible ? a.rank : null)
    })
    return () => { cancelled = true }
  }, [seasonId])

  const podium = rows.filter((r) => r.rank <= 3).sort((a, b) => a.rank - b.rank)
  const rest = rows.filter((r) => r.rank > 3)
  const currentSeason = seasons.find((s) => s.id === seasonId)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-extrabold text-[26px] tracking-tight text-neutral-900">Leaderboard</h1>
          <button onClick={onBack} className="text-[11px] font-bold text-amber-700 hover:underline whitespace-nowrap">← Your Result</button>
        </div>
        {currentSeason && (
          <span className="inline-block mt-2 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-extrabold text-[10px] uppercase tracking-widest">
            {currentSeason.seasonName}
          </span>
        )}
      </div>

      {seasons.length > 1 && (
        <select
          value={seasonId ?? ''}
          onChange={(e) => setSeasonId(e.target.value)}
          className="bg-white border border-neutral-200 rounded-xl px-3 py-2.5 text-[13px] font-bold shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 self-start"
        >
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>{s.seasonName}</option>
          ))}
        </select>
      )}

      <TopThreePodium entries={podium} />

      <LeaderboardTable rows={rest} loading={loading} myRank={myRank} />

      {!loading && <Pagination page={page} totalPages={totalPages} onChange={goToPage} />}

      {currentSeason && (
        <div className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
          <span className="text-[28px]">🏆</span>
          <div className="flex-1">
            <p className="font-extrabold text-[13px] text-neutral-900">{currentSeason.seasonName} Rewards</p>
            <p className="text-[12px] text-neutral-500">Top ranked users receive CPT rewards</p>
          </div>
          <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-[11px] uppercase tracking-widest ${currentSeason.status === 'ended' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            {currentSeason.status === 'ended' ? 'Ended' : 'Active'}
            <Icon name={currentSeason.status === 'ended' ? 'event_available' : 'schedule'} className="text-[14px]" />
          </span>
        </div>
      )}
    </div>
  )
}

// ─── Top-level state machine ───────────────────────────────────────────
export default function Allocation() {
  const [authed, setAuthed] = useState(!!getAllocationToken())
  const [view, setView] = useState('result') // 'result' | 'leaderboard'
  const [seasons, setSeasons] = useState([])
  const [seasonId, setSeasonId] = useState(null)
  const [seasonsLoading, setSeasonsLoading] = useState(true)

  useEffect(() => {
    if (!authed) return
    setSeasonsLoading(true)
    getSeasonsWithSnapshot()
      .then((list) => {
        setSeasons(list)
        if (list.length > 0) setSeasonId(list[0].id)
      })
      .finally(() => setSeasonsLoading(false))
  }, [authed])

  const handleLogout = () => {
    clearAllocationSession()
    setAuthed(false)
    setView('result')
  }

  return (
    <PageShell>
      {!authed ? (
        <LoginStage onVerified={() => setAuthed(true)} />
      ) : seasonsLoading ? (
        <div className="h-64 bg-surface-container-high animate-pulse rounded-lg max-w-md mx-auto w-full" />
      ) : seasons.length === 0 ? (
        <div className="border-4 border-dashed border-on-background/25 bg-surface p-8 text-center max-w-md mx-auto w-full">
          <p className="font-bold text-[12px] uppercase tracking-widest text-on-surface-variant">No season results available yet</p>
        </div>
      ) : view === 'result' ? (
        <ResultStage
          seasons={seasons}
          seasonId={seasonId}
          setSeasonId={setSeasonId}
          onViewLeaderboard={() => setView('leaderboard')}
          onLogout={handleLogout}
        />
      ) : (
        <LeaderboardStage
          seasons={seasons}
          seasonId={seasonId}
          setSeasonId={setSeasonId}
          onBack={() => setView('result')}
        />
      )}
    </PageShell>
  )
}
